import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse, Stack, Chip, Button, Tooltip } from '@mui/material';
import {
  Description as SoapIcon,
  Medication as MedIcon,
  Vaccines as VaxIcon,
  Healing as ProcIcon,
  Science as LabIcon,
  Note as NoteIcon,
  Phone as CommIcon,
  Article as RecordIcon,
  LocalHospital as DischargeIcon,
  Receipt as InvoiceIcon,
  Send as StableDocsIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchivedIcon,
  Edit as EditIcon,
  OpenInFull as OpenIcon,
  ExpandMore as ExpandMoreIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, isToday, isYesterday, isThisWeek, isThisMonth, isThisYear } from 'date-fns';
import { TimelineEvent, TimelineKind, safeDate } from './TimelineRow';
import { formatCalendarDate } from '../../../utils/dateUtils';
import { SoapNote, SoapNoteStatus } from '../../../types/soap';
import ShareSentDetail, { DeliveryChip, deliveryProblem } from '../../../components/StableDocs/ShareSentDetail';

interface SpineProps {
  events: TimelineEvent[];
  canEdit: boolean;
  canArchive: boolean;
  onSoapView: (note: SoapNote) => void;
  onSoapEdit: (note: SoapNote) => void;
  onProcedureEdit: (proc: any) => void;
  onRecordEdit: (record: any) => void;
  onDischargeEdit?: (event: TimelineEvent) => void;
  onMenu?: (event: TimelineEvent, anchor: { top: number; left: number }) => void;
  onOpenFull: (event: TimelineEvent) => void;
  /** soapNoteId -> attached-file count; shows a paperclip on collapsed SOAP rows. */
  attachmentCountByNoteId?: Record<string, number>;
}

const KIND_META: Record<TimelineKind, { label: string; color: string; bg: string; icon: React.ReactElement; dot: string }> = {
  soap: { label: 'SOAP', color: '#1565C0', bg: '#E3F2FD', dot: '#1976D2', icon: <SoapIcon sx={{ fontSize: 14 }} /> },
  medication: { label: 'Rx', color: '#2E7D32', bg: '#E8F5E9', dot: '#43A047', icon: <MedIcon sx={{ fontSize: 14 }} /> },
  vaccination: { label: 'Vax', color: '#6A1B9A', bg: '#F3E5F5', dot: '#8E24AA', icon: <VaxIcon sx={{ fontSize: 14 }} /> },
  procedure: { label: 'Proc', color: '#C62828', bg: '#FFEBEE', dot: '#E53935', icon: <ProcIcon sx={{ fontSize: 14 }} /> },
  lab: { label: 'Lab', color: '#EF6C00', bg: '#FFF3E0', dot: '#FB8C00', icon: <LabIcon sx={{ fontSize: 14 }} /> },
  visit_note: { label: 'Visit', color: '#455A64', bg: '#ECEFF1', dot: '#546E7A', icon: <NoteIcon sx={{ fontSize: 14 }} /> },
  record: { label: 'Note', color: '#5D4037', bg: '#EFEBE9', dot: '#6D4C41', icon: <RecordIcon sx={{ fontSize: 14 }} /> },
  communication: { label: 'Comm', color: '#00838F', bg: '#E0F7FA', dot: '#00ACC1', icon: <CommIcon sx={{ fontSize: 14 }} /> },
  discharge: { label: 'Discharge', color: '#4527A0', bg: '#EDE7F6', dot: '#5E35B1', icon: <DischargeIcon sx={{ fontSize: 14 }} /> },
  invoice: { label: 'Invoice', color: '#1565C0', bg: '#E3F2FD', dot: '#1976D2', icon: <InvoiceIcon sx={{ fontSize: 14 }} /> },
  stabledocs: { label: 'StableDocs', color: '#9A5A1E', bg: '#FBEFE3', dot: '#C9772B', icon: <StableDocsIcon sx={{ fontSize: 14 }} /> },
};

const dateBucket = (iso: string): string => {
  const d = safeDate(iso);
  if (!d) return 'Unknown';
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  if (isThisWeek(d, { weekStartsOn: 1 })) return 'This week';
  if (isThisMonth(d)) return 'This month';
  if (isThisYear(d)) return format(d, 'MMMM');
  return format(d, 'yyyy');
};

const formatTime = (iso: string) => {
  const d = safeDate(iso);
  if (!d) return '—';
  if (isToday(d) || isYesterday(d)) return format(d, 'h:mm a');
  return format(d, 'MMM d');
};

const formatExactDate = (iso: string) => {
  const d = safeDate(iso);
  if (!d) return 'Unknown date';
  return `${format(d, 'MMM d, yyyy · h:mm a')} · ${formatDistanceToNow(d, { addSuffix: true })}`;
};

interface ExpandedDetailProps {
  event: TimelineEvent;
}

/**
 * Compact inline detail body. For SOAP we render a 4-quadrant SOAP grid;
 * for meds we show dose/freq/refills; for everything else we fall back to a
 * narrative paragraph. The "Open full" button promotes to the heavy
 * existing card via the drawer when the vet needs more.
 */
const ExpandedDetail: React.FC<ExpandedDetailProps> = ({ event }) => {
  const item = event.raw;

  if (event.kind === 'soap') {
    const subjective = item.subjective?.aiSummary || item.subjective?.notes;
    const objective = item.objective?.aiSummary || item.objective?.notes || item.objective?.findings;
    const assessment = item.assessment?.aiSummary || item.assessment?.primaryDiagnosis;
    const plan = item.plan?.planNotes || item.plan?.notes || item.plan?.followUpInstructions;
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, pt: 1 }}>
        <DetailField label="Subjective" value={subjective} />
        <DetailField label="Objective" value={objective} />
        <DetailField label="Assessment" value={assessment} />
        <DetailField label="Plan" value={plan} />
      </Box>
    );
  }

  if (event.kind === 'medication') {
    const meds = item.itemType === 'medication-group' ? item.medications : [item];
    return (
      <Stack spacing={1} sx={{ pt: 1 }}>
        {meds.map((m: any) => (
          <Box key={m.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', p: 1, bgcolor: 'rgba(46,125,50,0.04)', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.name}</Typography>
            {[m.dosage, m.frequency, m.route].filter(Boolean).map((v: string, i: number) => (
              <Chip key={i} size="small" label={v} sx={{ height: 20, fontSize: '0.7rem' }} />
            ))}
            {m.isRx && <Chip size="small" label="Rx" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />}
            {typeof m.refillsAuthorized === 'number' && (
              <Chip size="small" label={`${m.refillsAuthorized} refill${m.refillsAuthorized === 1 ? '' : 's'}`} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
        ))}
      </Stack>
    );
  }

  if (event.kind === 'vaccination') {
    // A CALENDAR day: its date part, never re-zoned (DATE-KINDS §1.1).
    const nextDue = formatCalendarDate(item.nextDueDate, 'MMM d, yyyy');
    return (
      <Box sx={{ pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.vaccineName || item.name}</Typography>
        {nextDue && (
          <Chip size="small" label={`Next: ${nextDue}`} sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        {item.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: 0.5 }}>
            {item.notes}
          </Typography>
        )}
      </Box>
    );
  }

  if (event.kind === 'stabledocs') return <ShareSentDetail share={item} />;

  if (event.kind === 'procedure') {
    return (
      <Box sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {item.description || 'No description'}
        </Typography>
      </Box>
    );
  }

  // Visit notes + raw records
  const notes = item.notes || '';
  return (
    <Box sx={{ pt: 1 }}>
      {item.tags && item.tags.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }} useFlexGap>
          {item.tags.map((tag: string, i: number) => (
            <Chip key={i} label={tag} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          ))}
        </Stack>
      )}
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{notes}</Typography>
    </Box>
  );
};

const DetailField: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontSize: '0.68rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: 'text.secondary',
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontSize: '0.85rem',
        color: value ? 'text.primary' : 'text.disabled',
        whiteSpace: 'pre-wrap',
        lineHeight: 1.4,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

interface SpineRowProps {
  event: TimelineEvent;
  isLast: boolean;
  isOpen: boolean;
  canEdit: boolean;
  canArchive: boolean;
  onToggle: () => void;
  onMenu?: (event: TimelineEvent, anchor: { top: number; left: number }) => void;
  onOpenFull: (event: TimelineEvent) => void;
  onEdit?: (event: TimelineEvent) => void;
  attachmentCount?: number;
}

const SpineRow: React.FC<SpineRowProps> = ({ event, isLast, isOpen, canEdit, canArchive, onToggle, onMenu, onOpenFull, onEdit, attachmentCount = 0 }) => {
  const meta = KIND_META[event.kind] ?? KIND_META.record;
  const handleMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!onMenu) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onMenu(event, { top: rect.bottom, left: rect.right });
  };

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'grid',
        gridTemplateColumns: '78px 1fr',
        alignItems: 'flex-start',
        mb: 0.75,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isOpen ? meta.dot : 'transparent',
        bgcolor: isOpen ? '#fff' : 'transparent',
        boxShadow: isOpen ? '0 2px 10px rgba(0,0,0,0.05)' : 'none',
        cursor: 'pointer',
        transition: 'all 140ms cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: event.isArchived ? 0.55 : 1,
        '&:hover': {
          bgcolor: isOpen ? '#fff' : 'rgba(0,0,0,0.025)',
        },
      }}
      data-testid={`spine-row-${event.id}`}
    >
      {/* Date column with a small color accent dot */}
      <Box sx={{ pt: 1.25, pl: 1, pr: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Tooltip title={formatExactDate(event.date)} placement="left" arrow>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: meta.dot,
              flexShrink: 0,
            }}
          />
        </Tooltip>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {formatTime(event.date)}
        </Typography>
      </Box>

      {/* Card column */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.25 }}>
          <Chip
            icon={meta.icon}
            label={meta.label}
            size="small"
            sx={{
              bgcolor: meta.bg,
              color: meta.color,
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 700,
              borderRadius: 1,
              flexShrink: 0,
              '& .MuiChip-icon': { color: meta.color, ml: 0.5 },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {event.title}
              </Typography>
              {event.isArchived && (
                <Tooltip title="Archived"><ArchivedIcon sx={{ fontSize: 14, color: 'text.disabled' }} /></Tooltip>
              )}
            </Box>
            {!isOpen && event.summary && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3 }}>
                {event.summary}
              </Typography>
            )}
          </Box>
          {event.kind === 'stabledocs' && deliveryProblem(event.raw.deliveryStatus) && (
            <DeliveryChip status={event.raw.deliveryStatus} />
          )}
          {attachmentCount > 0 && (
            <Tooltip title={`${attachmentCount} attached file${attachmentCount === 1 ? '' : 's'}`}>
              <Chip
                icon={<AttachFileIcon sx={{ fontSize: 13 }} />}
                label={attachmentCount}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.66rem',
                  fontWeight: 700,
                  borderRadius: 1,
                  flexShrink: 0,
                  '& .MuiChip-icon': { ml: 0.4 },
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            </Tooltip>
          )}
          {event.by && (
            <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {event.by}
            </Typography>
          )}
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: 'text.disabled',
              transition: 'transform 160ms',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>

        <Collapse in={isOpen} unmountOnExit>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <ExpandedDetail event={event} />
            <Stack direction="row" spacing={1} sx={{ pt: 1.5, justifyContent: 'flex-end' }}>
              {canEdit && onEdit && (
                <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={(e) => { e.stopPropagation(); onEdit(event); }} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  Edit
                </Button>
              )}
              <Button size="small" startIcon={<OpenIcon sx={{ fontSize: 14 }} />} onClick={(e) => { e.stopPropagation(); onOpenFull(event); }} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                Open full
              </Button>
              {/* A send has nothing to archive or restore, so it gets no row menu. */}
              {onMenu && canArchive && event.kind !== 'stabledocs' && (
                <IconButton size="small" onClick={handleMenu}>
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Stack>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

/**
 * Vertical "spine" timeline. Each event is a colored dot on a continuous
 * line plus an event card. The line gives the chart its narrative shape —
 * vets can scan the dot column to spot bursts of activity. Click a card to
 * expand inline so adjacent context stays visible.
 *
 * Date dividers ("Today", "This week", "May 2026") group runs of events so
 * scrolling has natural waypoints.
 */
const TimelineSpine: React.FC<SpineProps> = ({
  events,
  canEdit,
  canArchive,
  onSoapView,
  onSoapEdit,
  onProcedureEdit,
  onRecordEdit,
  onDischargeEdit,
  onMenu,
  onOpenFull,
  attachmentCountByNoteId,
}) => {
  const [openId, setOpenId] = useState<string | null>(null);

  const onEdit = (event: TimelineEvent) => {
    if (event.kind === 'soap') return onSoapEdit(event.raw);
    if (event.kind === 'procedure') return onProcedureEdit(event.raw);
    if (event.kind === 'visit_note' || event.kind === 'record') return onRecordEdit(event.raw);
    if (event.kind === 'discharge') return onDischargeEdit?.(event);
  };

  // The inline Edit action only renders for kinds that actually have an inline
  // edit handler in onEdit() below — otherwise the button did nothing. The
  // remaining kinds (medication/vaccination/lab/communication/invoice) are
  // edited via "Open full" → their card, so they show only that action. A
  // completed SOAP and a finalized discharge are read-only (still viewable via
  // Open full); discharge also needs its edit handler wired.
  const isEventEditable = (event: TimelineEvent): boolean => {
    switch (event.kind) {
      case 'soap':
        return (event.raw as any)?.status !== SoapNoteStatus.COMPLETED;
      case 'procedure':
      case 'visit_note':
      case 'record':
        return true;
      case 'discharge':
        return !!onDischargeEdit && (event.raw as any)?.status !== 'FINALIZED';
      default:
        return false;
    }
  };

  // Group events into date buckets while preserving order
  const groups: Array<{ bucket: string; events: TimelineEvent[] }> = [];
  let currentBucket: string | null = null;
  for (const ev of events) {
    const b = dateBucket(ev.date);
    if (b !== currentBucket) {
      groups.push({ bucket: b, events: [] });
      currentBucket = b;
    }
    groups[groups.length - 1].events.push(ev);
  }

  let globalIndex = 0;
  const totalEvents = events.length;

  return (
    <Box sx={{ width: '100%' }}>
      {groups.map((group, gIdx) => (
        <Box key={`${group.bucket}-${gIdx}`}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              px: 1,
              position: 'sticky',
              // Offset for the sticky patient banner above
              top: 56,
              zIndex: 2,
              bgcolor: 'rgba(250, 249, 246, 0.95)',
              backdropFilter: 'blur(6px)',
              borderBottom: '1px solid',
              borderColor: 'rgba(139,94,52,0.06)',
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontWeight: 700,
                fontSize: '0.68rem',
                color: 'text.secondary',
                letterSpacing: 0.8,
              }}
            >
              {group.bucket}
            </Typography>
            <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'rgba(139,94,52,0.06)' }} />
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums' }}>
              {group.events.length}
            </Typography>
          </Box>
          {group.events.map((event) => {
            const isLast = globalIndex === totalEvents - 1;
            const isOpen = openId === `${event.kind}-${event.id}`;
            const node = (
              <SpineRow
                key={`${event.kind}-${event.id}`}
                event={event}
                isLast={isLast}
                isOpen={isOpen}
                canEdit={canEdit}
                canArchive={canArchive}
                onToggle={() => setOpenId(isOpen ? null : `${event.kind}-${event.id}`)}
                onMenu={onMenu}
                onOpenFull={onOpenFull}
                onEdit={isEventEditable(event) ? onEdit : undefined}
                attachmentCount={event.kind === 'soap' ? (attachmentCountByNoteId?.[event.id] || 0) : 0}
              />
            );
            globalIndex += 1;
            return node;
          })}
        </Box>
      ))}
    </Box>
  );
};

// Suppress unused-import warnings for view callbacks reserved for "Open full"
void ((_: SoapNote) => {});

export default TimelineSpine;
